import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import {
    listNotifications,
    markNotificationRead,
    markAllNotificationsRead,
} from "@/lib/promo";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";

const REFRESH_MS = 45000;

const TYPE_ICON = {
    LAUNCH_PROMO_AVAILABLE: "🎁",
    LAUNCH_PROMO_ACTIVATED: "✨",
    LAUNCH_PROMO_EXPIRING: "⏰",
    LAUNCH_PROMO_EXPIRED: "⚠️",
    FLASH_CREDITS_ISSUED: "⚡",
    FLASH_BOOST_ACTIVATED: "🚀",
    FLASH_BOOST_EXPIRED: "🕘",
    GENERIC: "🔔",
};

export function NotificationBell({ variant = "light" }) {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ notifications: [], unread_count: 0 });

    const refresh = useCallback(async () => {
        try {
            const d = await listNotifications({ limit: 10 });
            setData(d);
        } catch (e) {
            // Silent — bell is a nicety and we don't want to spam toasts.
        }
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, REFRESH_MS);
        return () => clearInterval(t);
    }, [refresh]);

    const onCta = async (n) => {
        try {
            if (!n.is_read) {
                await markNotificationRead(n.id);
            }
        } catch (_) {}
        setOpen(false);
        refresh();
        if (n.cta_url) nav(n.cta_url);
    };

    const onMarkAll = async () => {
        try {
            await markAllNotificationsRead();
            toast.success("Marked all as read");
            refresh();
        } catch (e) {
            toast.error("Couldn't mark all read");
        }
    };

    const isDark = variant === "dark";
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className={`relative flex h-9 w-9 items-center justify-center rounded-full border ${isDark ? "border-neutral-700 bg-neutral-800 text-white hover:bg-neutral-700" : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"}`}
                    aria-label="Notifications"
                    data-testid="notif-bell-btn"
                >
                    <Bell size={16} />
                    {data.unread_count > 0 && (
                        <span
                            className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white ring-2 ring-white"
                            data-testid="notif-unread-count"
                        >
                            {data.unread_count > 99 ? "99+" : data.unread_count}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-80 p-0"
                data-testid="notif-dropdown"
            >
                <div className="flex items-center justify-between border-b border-neutral-200 p-3">
                    <div className="text-sm font-bold text-neutral-900">Notifications</div>
                    {data.unread_count > 0 && (
                        <button
                            onClick={onMarkAll}
                            className="text-xs font-semibold text-orange-600 hover:text-orange-700"
                            data-testid="notif-mark-all-read"
                        >
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {data.notifications.length === 0 && (
                        <div className="px-4 py-10 text-center text-xs text-neutral-500">
                            You're all caught up 🎉
                        </div>
                    )}
                    {data.notifications.map((n) => (
                        <div
                            key={n.id}
                            className={`flex gap-2 border-b border-neutral-100 p-3 last:border-b-0 ${n.is_read ? "bg-white" : "bg-orange-50/40"}`}
                            data-testid={`notif-item-${n.id}`}
                        >
                            <div className="text-lg leading-none">{TYPE_ICON[n.type] || "🔔"}</div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="text-sm font-semibold text-neutral-900">
                                        {n.title}
                                    </div>
                                    {!n.is_read && (
                                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                                    )}
                                </div>
                                <div className="mt-0.5 text-xs text-neutral-600">{n.body}</div>
                                <div className="mt-1 flex items-center justify-between text-[10px] text-neutral-400">
                                    <span>{formatDate(n.created_at)}</span>
                                    {n.cta_label && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 text-[11px] font-semibold text-orange-600 hover:text-orange-700"
                                            onClick={() => onCta(n)}
                                            data-testid={`notif-cta-${n.id}`}
                                        >
                                            {n.cta_label} →
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default NotificationBell;
